import { Localized, useLocalization } from "@fluent/react";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { ChangeEvent, KeyboardEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AuthPageLayout } from "./AuthPageLayout";
import { APIError } from "./app/appTypes";
import { useAppDispatch } from "./app/hooks";
import { LanguageSelector } from "./LanguageSelector";
import { showError } from "./features/notifications/notificationsSlice";
import { registerSsoUser } from "./features/user/userSlice";

type ViewState = "registration" | "success";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SsoRegister = () => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const [searchParams] = useSearchParams();
    const registrationId = searchParams.get("registrationId") ?? "";
    const [viewState, setViewState] = useState<ViewState>("registration");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [passwordConfirmationError, setPasswordConfirmationError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const missingRegistrationId = registrationId.length === 0;

    const validateEmail = () => {
        setEmailError(emailRegex.test(email) ? "" : "email-incorrect");
    };

    const validatePassword = () => {
        setPasswordError(password.trim() ? "" : "field-is-required");
    };

    const validatePasswordConfirmation = () => {
        setPasswordConfirmationError(password === passwordConfirmation ? "" : "password-do-not-match");
    };

    const isFormValid = () => {
        const emailValid = emailRegex.test(email);
        const passwordValid = password.trim().length > 0;
        const passwordConfirmationValid = password === passwordConfirmation;

        setEmailError(emailValid ? "" : "email-incorrect");
        setPasswordError(passwordValid ? "" : "field-is-required");
        setPasswordConfirmationError(passwordConfirmationValid ? "" : "password-do-not-match");

        return emailValid && passwordValid && passwordConfirmationValid && !missingRegistrationId;
    };

    const canSubmit = useMemo(() => {
        return !missingRegistrationId
            && !!email
            && !!password
            && !!passwordConfirmation
            && !emailError
            && !passwordError
            && !passwordConfirmationError;
    }, [email, password, passwordConfirmation, emailError, passwordError, passwordConfirmationError, missingRegistrationId]);

    const handleRegister = async () => {
        if (!isFormValid()) {
            return;
        }

        setSubmitting(true);

        try {
            await dispatch(registerSsoUser({
                registrationId,
                email,
                password,
                passwordConfirmation,
            })).unwrap();

            setViewState("success");
        } catch (error) {
            const payload = error as APIError;

            if (payload?.kind === "http" && payload.body) {
                try {
                    const apiResponse = JSON.parse(payload.body);
                    dispatch(showError(apiResponse.title ?? "error-registration-failed"));
                } catch {
                    dispatch(showError("error-registration-failed"));
                }
            } else if (payload?.kind === "connection") {
                dispatch(showError("connection-issue"));
            } else {
                dispatch(showError("error-registration-failed"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleEnter = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
            handleRegister();
        }
    };

    return (
        <AuthPageLayout>
            <Grid container direction="column" spacing={2}>
                {viewState === "success" ? (
                    <Grid item>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                py: 3,
                                px: 1,
                                gap: 2,
                            }}
                        >
                            <MarkEmailReadIcon sx={{ fontSize: 72, color: "success.main" }} />
                            <Typography variant="h5" fontWeight={700}>
                                <Localized id="sso-register-success-title">
                                    <span>Check your email</span>
                                </Localized>
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                <Localized id="sso-register-success-message">
                                    <span>A confirmation link has been sent to your email address. Open the message and confirm the link to finish registration.</span>
                                </Localized>
                            </Typography>
                            <Button
                                component={Link}
                                to="/login"
                                size="large"
                                variant="contained"
                                color="primary"
                                sx={{ mt: 1 }}
                            >
                                <Localized id="back-to-login">
                                    <span>Back to login</span>
                                </Localized>
                            </Button>
                            <Box sx={{ width: "100%", pt: 1 }}>
                                <LanguageSelector />
                            </Box>
                        </Box>
                    </Grid>
                ) : (
                    <>
                <Grid item>
                    <Typography variant="h6" gutterBottom>
                        <Localized id="sso-register-title">
                            <span>SSO registration required</span>
                        </Localized>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="sso-register-description">
                            <span>Your Microsoft account is not registered in the application yet. Provide your email address and a security password to complete registration.</span>
                        </Localized>
                    </Typography>
                </Grid>

                {missingRegistrationId && (
                    <Grid item>
                        <Typography variant="body2" color="error.main">
                            <Localized id="sso-register-missing-registration-id">
                                <span>Missing registration identifier. Restart the SSO login flow.</span>
                            </Localized>
                        </Typography>
                    </Grid>
                )}

                <Grid item>
                    <TextField
                        value={email}
                        fullWidth
                        required
                        type="email"
                        onBlur={validateEmail}
                        error={!!emailError}
                        helperText={emailError ? l10n.getString(emailError, null, "error") : ""}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                        inputProps={{ maxLength: 100, autoComplete: "email" }}
                        label={<Localized id="user-email">_Email</Localized>}
                        onKeyDown={handleEnter}
                    />
                </Grid>

                <Grid item>
                    <TextField
                        value={password}
                        fullWidth
                        required
                        type="password"
                        onBlur={validatePassword}
                        error={!!passwordError}
                        helperText={passwordError ? l10n.getString(passwordError, null, "error") : l10n.getString("sso-register-security-password-help", null, "Set a security password for your application account.")}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
                        inputProps={{ maxLength: 100, autoComplete: "new-password" }}
                        label={<Localized id="sso-register-security-password"><span>_Security password</span></Localized>}
                        onKeyDown={handleEnter}
                    />
                </Grid>

                <Grid item>
                    <TextField
                        value={passwordConfirmation}
                        fullWidth
                        required
                        type="password"
                        onBlur={validatePasswordConfirmation}
                        error={!!passwordConfirmationError}
                        helperText={passwordConfirmationError ? l10n.getString(passwordConfirmationError, null, "error") : ""}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setPasswordConfirmation(event.target.value)}
                        inputProps={{ maxLength: 100, autoComplete: "new-password" }}
                        label={<Localized id="sso-register-security-password-confirm"><span>_Confirm security password</span></Localized>}
                        onKeyDown={handleEnter}
                    />
                </Grid>

                <Grid item>
                    <Button
                        onClick={handleRegister}
                        color="primary"
                        size="large"
                        variant="contained"
                        disabled={!canSubmit || submitting}
                        fullWidth
                    >
                        <Localized id="register">
                            <span>Register</span>
                        </Localized>
                    </Button>
                </Grid>

                <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 20, marginBottom: 10 }} />

                <Grid item textAlign="center" sx={{ bgcolor: "grey.100", borderRadius: 2, py: 1.5, marginLeft: 2 }}>
                    <Typography variant="body2" component="div" sx={{ whiteSpace: "nowrap", fontWeight: "800", display: "inline-flex", alignItems: "center" }}>
                        <Localized id="have-account">
                            <span>Masz już konto?</span>
                        </Localized>{" "}
                        <Button
                            component={Link}
                            to="/login"
                            size="small"
                            variant="contained"
                            color="primary"
                            sx={{ ml: 2, px: 1.25, py: 0.25, minWidth: "auto", textTransform: "none", display: "inline-flex" }}
                        >
                            <Localized id="login">
                                <span>_Login</span>
                            </Localized>
                        </Button>
                    </Typography>
                </Grid>

                <Grid item textAlign="center">
                    <LanguageSelector />
                </Grid>
                    </>
                )}
            </Grid>

            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={submitting}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </AuthPageLayout>
    );
};

export default SsoRegister;